import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { MapPin, ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

/**
 * Public renderer for CMS pages at /p/:slug.
 * - Fetches GET /api/pages/{slug}
 * - Renders `content` as HTML (sanitized via DOMPurify — safe against script
 *   injection while allowing basic formatting tags h1-h6, p, ul, ol, li, a,
 *   strong, em, br, hr).
 */
export default function PublicPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: "", page: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/${encodeURIComponent(slug)}`);
        if (alive) setState({ loading: false, error: "", page: data });
      } catch (e) {
        if (alive)
          setState({
            loading: false,
            error: e?.response?.data?.detail || "Page not found",
            page: null,
          });
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  // The content field may contain "\n" plain-text or an HTML fragment. Sanitize
  // aggressively — only formatting tags allowed, no scripts / iframes / event
  // handlers. Line breaks in plain text are preserved via a whitespace class.
  const html = useMemo(() => {
    if (!state.page?.content) return "";
    return DOMPurify.sanitize(state.page.content, {
      ALLOWED_TAGS: [
        "h1", "h2", "h3", "h4", "h5", "h6",
        "p", "ul", "ol", "li", "a", "strong", "em", "b", "i",
        "br", "hr", "blockquote", "code", "pre", "span", "div",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class"],
    });
  }, [state.page]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-page-home-link">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-md shadow-primary/20">
              <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="text-base font-extrabold tracking-tight">NearMe</span>
          </Link>
          <Button asChild variant="ghost" size="sm" data-testid="public-page-back-btn">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Link>
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {state.loading ? (
          <div className="text-muted-foreground flex items-center gap-2" data-testid="public-page-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : state.error ? (
          <div
            data-testid="public-page-error"
            className="bg-card border border-border rounded-2xl p-10 text-center"
          >
            <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-amber-600" />
            <div className="text-lg font-bold mb-1">Page not found</div>
            <div className="text-sm text-muted-foreground mb-4">{state.error}</div>
            <Button asChild variant="outline">
              <Link to="/">Go home</Link>
            </Button>
          </div>
        ) : (
          <article data-testid="public-page-content">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
              {state.page.title}
            </h1>
            <div
              className="prose max-w-none prose-headings:font-bold prose-a:text-primary whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: html }}
            />
            {state.page.updated_at && (
              <div className="text-xs text-muted-foreground mt-10 border-t border-border pt-4">
                Last updated {new Date(state.page.updated_at).toLocaleDateString()}
              </div>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
